import React, { useState, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import List from 'partial/components/List';
import SearchBar from 'partial/components/input/SearchBar';
import ThumbIcon from 'partial/components/ThumbIcon';
import { head, pickBy } from 'lodash';
import { usePersistState } from 'helper';
import Pagination from 'partial/components/Pagination';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import EmptyPage from 'partial/components/EmptyPage';
import classNames from 'classnames';
import SubscriberProfile from './components/SubscriberProfile';
import { useOrganizationList } from './hooks';

function SubscribersDashboard() {
  const { setSubLabel } = useModuleContext();
  const [selected, setSelected] = usePersistState('subscriber-id', '', {
    keepOnUnmount: false,
  });
  const [filter, setFilter] = useState({ q: '' });
  const [page, setPage] = useState({ page: '' });
  const payload = useMemo(
    () =>
      pickBy({
        ...filter,
        ...page,
        per_page: 15,
        sort_by: 'name',
        order: 'asc',
      }),
    [filter, page],
  );

  useEffect(() => {
    setPage({ page: 1 });
  }, [filter]);

  const [isLoading, list, meta] = useOrganizationList(payload);

  const navigate = useNavigate();
  const onSubSelect = (item) => {
    setSelected(item?.user?.uuid);
    navigate(`${item?.user?.uuid}`);
  };

  useEffect(() => {
    if (selected) return;
    setSelected(head(list)?.user?.uuid);
  }, [list, selected, setSelected]);

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} subscriber${meta?.total === 1 ? '' : 's'}`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  return (
    <section className="min-h-[calc(100vh_-_1rem)] flex p-6 bg-white drop-shadow rounded-lg flex-grow static">
      <div className="pr-4 border-r border-gray-50 min-w-[300px] flex flex-col">
        <SearchBar
          name="q"
          value={filter.q}
          onChange={setFilter}
          inputClassName="py-[1.05rem] !w-full"
          containerClassName="mb-4"
        />
        <List
          data={list}
          isLoading={isLoading}
          selectedId={selected}
          onSelectRow={onSubSelect}
          containerClassName="!w-full"
          render={(item) => {
            const name = item?.name;
            return (
              <div className="flex items-center gap-2 pl-2 py-1 w-full">
                <ThumbIcon
                  className="h-8 w-8 rounded-full"
                  alt={name}
                  url={item?.user?.profile?.avatar || ''}
                />
                <div className="max-w-[200px]">
                  <div className="text-sm text-ellipsis overflow-hidden whitespace-nowrap">
                    {name}
                  </div>
                  <div
                    className={classNames(
                      'text-xs font-semibold',
                      item?.is_banned ? 'text-danger-400' : 'text-success-400',
                    )}
                  >
                    {item?.is_banned ? 'Banned' : 'Active'}
                  </div>
                </div>
              </div>
            );
          }}
          selectedKey="user.uuid"
        />
        <div className="self-center mt-8">
          <Pagination value={meta} onChange={setPage} withPage={false} />
        </div>
      </div>
      <Routes>
        <Route path=":id/*" element={<SubscriberProfile />} />
        {list?.length > 0 && (
          <Route
            index
            element={<Navigate replace to={`${head(list)?.user?.uuid}`} />}
          />
        )}
        <Route
          path="*"
          element={
            <div className="flex-1">
              <EmptyPage />
            </div>
          }
        />
      </Routes>
    </section>
  );
}

export default SubscribersDashboard;
